/**
 * The public legal documents: Terms of Service, Privacy Policy and Refund Policy.
 *
 * English is the authoritative version. The seller identity and contact details
 * come from `siteInfo` (VITE_ env vars), so the documents never need editing when
 * those change; optional details (phone, EU/UK representatives, address) are
 * printed only once configured.
 *
 * The three documents cross-reference each other and MUST stay consistent — in
 * particular the credit rules (no expiry, 14-day withdrawal with a pro-rata refund
 * of unused credits, automatic refund of failed operations, reversal on
 * chargeback) appear in both the Terms and the Refund Policy, and every factual
 * statement about data handling must match what the code actually does (see the
 * compliance review of 2026-09-26).
 */
/* eslint-disable react-refresh/only-export-components -- a text module, not a component module; never hot-reloaded as UI */
import type { ReactNode } from "react";
import type { SiteInfo } from "../lib/siteInfo.js";
import {
  MERCHANT_OF_RECORD,
  MOR_BUYER_TERMS,
  docLink,
  external,
  mail,
  type LegalDocument,
  type LegalDocumentId,
  type LegalTexts
} from "./shared.js";

export { MERCHANT_OF_RECORD, type LegalDocument, type LegalDocumentId, type LegalSection } from "./shared.js";

/**
 * Translations, loaded on demand per language (each is a separate chunk). English
 * is the governing version; a translation mirrors it section for section.
 */
export const LEGAL_TRANSLATIONS: Record<string, () => Promise<{ texts: LegalTexts }>> = {
  ru: () => import("./translations/ru.js"),
  de: () => import("./translations/de.js"),
  es: () => import("./translations/es.js"),
  fr: () => import("./translations/fr.js"),
  pt: () => import("./translations/pt.js"),
  ja: () => import("./translations/ja.js"),
  zh: () => import("./translations/zh.js")
};

function operator(site: SiteInfo): ReactNode {
  return (
    <>
      <strong>{site.entityName}</strong>,{" "}
      {site.entityIsIndividual ? <>an individual based in {site.entityCountry}</> : <>registered in {site.entityCountry}</>}
      {site.entityAddress ? <> ({site.entityAddress})</> : null}
    </>
  );
}

/** "email" or "email or phone" — how to reach the operator. */
function contact(site: SiteInfo): ReactNode {
  return (
    <>
      {mail(site.supportEmail)}
      {site.supportPhone ? <> or by phone at {site.supportPhone}</> : null}
    </>
  );
}

function representatives(site: SiteInfo): ReactNode {
  if (!site.euRepresentative && !site.ukRepresentative) return null;
  return (
    <p>
      {site.euRepresentative ? (
        <>
          Our representative in the European Union (Article 27 GDPR and Article 13 of the Digital Services Act) is{" "}
          {site.euRepresentative}.{" "}
        </>
      ) : null}
      {site.ukRepresentative ? (
        <>Our representative in the United Kingdom (Article 27 UK GDPR) is {site.ukRepresentative}. </>
      ) : null}
      You may contact them instead of us about any matter covered by those laws.
    </p>
  );
}

/** The English (governing) documents. */
export const englishTexts: LegalTexts = {
  terms: termsOfService,
  privacy: privacyPolicy,
  refund: refundPolicy
};

/** Builds one document, in English unless a loaded translation is passed. */
export function buildLegalDocument(id: LegalDocumentId, site: SiteInfo, texts: LegalTexts = englishTexts): LegalDocument {
  return texts[id](site);
}

function termsOfService(site: SiteInfo): LegalDocument {
  return {
    title: "Terms of Service",
    summary:
      "The rules for using Lorvok: who can use it, how credits and payments work, what content is not allowed, how we moderate, and who owns what you create.",
    sections: [
      {
        heading: "1. Who we are",
        body: (
          <>
            <p>
              Lorvok (the “Service”), available at {site.siteUrl}, is operated by {operator(site)} (“we”, “us”). You
              can reach us at {contact(site)}.
            </p>
            {representatives(site)}
            <p>
              By creating an account, buying credits or otherwise using the Service you agree to these Terms. If you
              do not agree, do not use the Service.
            </p>
          </>
        )
      },
      {
        heading: "2. The Service",
        body: (
          <>
            <p>
              Lorvok is an AI tool for creating interactive visual novels. You describe a story idea and the Service
              uses third-party artificial-intelligence models to generate a branching story — scenes, choices,
              endings, characters and illustrations — which you can play, edit, keep private or publish to the public
              catalog. Anyone can read published novels; creating and editing novels requires an account and credits.
            </p>
            <p>
              <strong>All stories and images on Lorvok are generated by AI</strong> and may have been edited by their
              authors. We label them as AI-generated and embed machine-readable markers in generated images. You may
              not remove these labels or markers, or present AI-generated content as real footage or as a true account
              of real events or people.
            </p>
          </>
        )
      },
      {
        heading: "3. Eligibility and accounts",
        body: (
          <>
            <p>
              You must be at least <strong>18 years old</strong> to create an account, use the Service and buy
              credits. By registering you confirm that you are; we delete accounts we find to belong to anyone under
              18.
            </p>
            <p>
              <strong>The Service is not offered in the Russian Federation or the United Kingdom</strong> and is not directed at users there.
              You may not use it from those countries or from any country or region subject to sanctions that prevent us or our
              payment provider from serving you, and we may block access from such places.
            </p>
            <p>
              You are responsible for keeping your login details secure and for all activity under your account.
              Provide a valid email address. One person may not use multiple accounts to abuse promotions or to evade
              a suspension.
            </p>
          </>
        )
      },
      {
        heading: "4. Credits, prices and payment",
        body: (
          <>
            <p>
              Generating and editing novels is paid for with credits. Credits are sold in packs; the current packs,
              their prices and the credit cost of each action are shown on the <a href="/pricing">Pricing</a> page
              before you pay. Prices are in US dollars. Depending on your country, VAT or sales tax is either included
              in the price or added at checkout; the checkout shows the final amount before you pay.
            </p>
            <p>
              Our order process is conducted by our online reseller {MERCHANT_OF_RECORD}. {MERCHANT_OF_RECORD} is the
              Merchant of Record for all our orders. {MERCHANT_OF_RECORD} handles customer service inquiries about
              payments and handles returns. {MERCHANT_OF_RECORD} processes your payment, collects and remits taxes and
              issues your invoice; your purchase is also subject to the{" "}
              {external(MOR_BUYER_TERMS, `${MERCHANT_OF_RECORD} Buyer Terms`)}. We never see or store your card
              details.
            </p>
            <p>
              Before paying you confirm that you accept these Terms and the {docLink("refund", "Refund Policy")}, and
              you ask us to start providing the Service immediately. Credits are added to your account automatically
              once {MERCHANT_OF_RECORD} confirms the payment, usually within a minute; if they do not appear within an
              hour, contact us with the order number from your receipt email.
            </p>
            <p>Credits:</p>
            <ul>
              <li>
                can only pay for Lorvok’s AI features — generating and editing novels. They are not money, electronic
                money or store credit: they cannot be withdrawn, redeemed or exchanged for cash, and cannot buy
                anything else, from us or from anyone else — nothing inside a novel is sold for credits;
              </li>
              <li>
                <strong>do not expire</strong> while your account is open;
              </li>
              <li>are personal to your account and cannot be transferred, sold or exchanged;</li>
              <li>are refundable only as described in the {docLink("refund", "Refund Policy")}.</li>
            </ul>
            <p>
              The credits for an action are reserved when you start it. If an action fails on our side (for example a
              generation that does not complete), its credits are returned to your balance automatically. If you
              cancel an action after it has started, its credits are not returned, because the work has already been
              carried out and paid for. Requests refused by our automated checks are never charged.
            </p>
            <p>
              To protect the Service we apply daily usage limits, per account and across the Service. When a limit is
              reached, paid actions become available again after 00:00 UTC.
            </p>
            <p>
              If a payment is reversed — refunded, charged back or disputed — the credits bought with it are removed
              from your balance. If those credits were already used, your account is suspended automatically; contact
              us to resolve it.
            </p>
          </>
        )
      },
      {
        heading: "5. Acceptable use and prohibited content",
        body: (
          <>
            <p>You may not use the Service to create, upload, publish or share content that:</p>
            <ul>
              <li>sexualises minors in any way, or depicts anyone who appears to be a minor in a sexual context;</li>
              <li>is sexually explicit or pornographic;</li>
              <li>
                uses a real person’s name, face, likeness or voice without their explicit consent — including face
                swaps, deepfakes and impersonation — or depicts real, identifiable people in a sexual, defamatory,
                harassing or deceptive way;
              </li>
              <li>is an intimate image of a person shared without their consent, whether real or AI-generated;</li>
              <li>promotes terrorism or violent extremism, or glorifies real acts of mass violence;</li>
              <li>incites hatred, violence or discrimination against people based on a protected characteristic;</li>
              <li>gives real-world instructions for weapons, self-harm, or other serious harm;</li>
              <li>
                infringes someone else’s copyright, trademark or other rights — for example reproducing a protected
                work, or using another creator’s characters or a brand in a way that is not permitted by law;
              </li>
              <li>harasses, threatens or exposes the private information of another person;</li>
              <li>is illegal where you live or in {site.entityCountry}.</li>
            </ul>
            <p>
              Lorvok creates illustrated, fictional characters only; you may not upload photographs of people to be
              turned into characters. Fiction may explore dark themes — crime, horror, conflict — as long as it stays
              within these limits. You also may not abuse the Service itself: attempt to bypass limits, safety filters
              or payments, scrape it, overload it, or resell access to it.
            </p>
          </>
        )
      },
      {
        heading: "6. How we moderate content",
        body: (
          <>
            <p>
              <strong>Automated checks.</strong> Story ideas, AI-edit requests, comments, profile names and bios,
              avatar descriptions and the text of a novel you publish are checked automatically — by a keyword filter
              and an AI classifier — before anything is charged or published. A request that fails is refused and is
              not charged. If you think a refusal is wrong, contact us and a person will review it.
            </p>
            <p>
              <strong>Reports.</strong> Anyone, with or without an account, can report a novel, a comment or a
              profile with the Report button on it, or by email to {mail(site.supportEmail)}. Reports are reviewed by
              a person.
            </p>
            <p>
              <strong>Measures.</strong> Depending on how serious and repeated a breach is, we may refuse a request,
              remove or unpublish content, remove comments, or suspend or close an account. We tell you by email what
              was restricted and why, whether the decision was automated, and how to contest it. To contest a
              decision, reply to that email within 6 months and a person will review it. If you are in the EU you may
              also use an out-of-court dispute settlement body certified under Article 21 of the Digital Services Act,
              or the courts.
            </p>
            <p>
              <strong>Intimate images.</strong> To request removal of an intimate image of you shared without your
              consent, including an AI-generated one, email {mail(site.supportEmail)} with the link, a statement that
              you did not consent, your full name (as a signature) and your contact details. We remove valid requests
              within 48 hours.
            </p>
            <p>
              <strong>Authorities.</strong> Our single point of contact for authorities and users under the EU
              Digital Services Act is {mail(site.supportEmail)} (English or Russian).
            </p>
          </>
        )
      },
      {
        heading: "7. Your content and AI output",
        body: (
          <>
            <p>
              <strong>What you own.</strong> As between you and us, you own your prompts, your edits and the novels
              generated for you, to the extent the law allows ownership of AI-assisted works. In some countries
              AI-generated material is protected by copyright only to the extent of your own creative contribution.
              We do not claim ownership of your content.
            </p>
            <p>
              <strong>Licence to us.</strong> You give us a worldwide, non-exclusive, royalty-free licence to host,
              store, process, display and distribute your content as needed to operate the Service — for example to
              generate it, save it, and show a novel you publish to other users. For published novels this licence
              lasts until you delete them or your account.
            </p>
            <p>
              <strong>Your responsibility.</strong> AI models can produce output that is inaccurate, unoriginal, or
              similar to existing works, and other users may receive similar output. You are responsible for your
              prompts and for reviewing what you publish or use outside the Service, including making sure it does
              not infringe anyone’s rights.
            </p>
            <p>
              <strong>Copyright complaints.</strong> If you believe content on Lorvok infringes your rights, report it
              or email {mail(site.supportEmail)} with the content’s link, a description of your work, and your contact
              details. We will review it and remove infringing content.
            </p>
          </>
        )
      },
      {
        heading: "8. Ending your account",
        body: (
          <>
            <p>
              You can stop using the Service and delete your account at any time (your author page → Edit profile →
              Delete account). <strong>Deleting your account permanently erases any remaining credits</strong>; if
              you bought credits in the last 14 days, contact us first to request a refund of the unused ones.
            </p>
            <p>
              We may suspend or close an account that breaches these Terms, is used fraudulently, or where required by
              law. If we close your account, we refund your unused paid credits, minus any amount you owe us (for
              example a charged-back payment) — unless the closure is for fraud or payment abuse.
            </p>
          </>
        )
      },
      {
        heading: "9. Changes to the Service and these Terms",
        body: (
          <p>
            We may change the Service or these Terms for valid reasons: changes in the law, in our service providers
            or AI models, to fix security or abuse problems, or to improve or add features. We will tell you by email
            at least 30 days before a material change that affects you. If you do not accept a change, you may close
            your account before it takes effect and we will refund your unused paid credits. Changes never reduce the
            credits you have already bought. If we discontinue the Service entirely we will give reasonable notice and
            refund unused paid credits. We work to keep the Service available and your data safe, but we do not
            guarantee uninterrupted operation.
          </p>
        )
      },
      {
        heading: "10. Liability",
        body: (
          <>
            <p>
              If you are a consumer, you have statutory rights — including the right to digital content and services
              that conform to the contract — and nothing in these Terms affects them. Nothing in these Terms excludes
              or limits our liability for death or personal injury caused by our negligence, for fraud, for damage
              caused intentionally or by gross negligence, or any other liability that cannot be excluded or limited
              by law.
            </p>
            <p>
              Subject to that, and to the extent permitted by law, we are not liable for indirect or consequential
              losses, and our total liability to you is limited to the greater of the amount you paid for credits in
              the 12 months before the claim and 100 US dollars.
            </p>
          </>
        )
      },
      {
        heading: "11. Governing law and disputes",
        body: (
          <p>
            These Terms are governed by the laws of {site.entityCountry}. If you are a consumer, you also keep the
            protection of the mandatory laws of your country of residence, and you may bring proceedings in the courts
            of your country of residence. Please contact us first — we aim to resolve complaints within 30 days.
          </p>
        )
      },
      {
        heading: "12. General",
        body: (
          <ul>
            <li>If a provision of these Terms is held invalid, the rest remains in effect.</li>
            <li>
              We may transfer these Terms to a successor operator of the Service if your rights are not reduced; we
              will tell you. You may not transfer your account.
            </li>
            <li>
              We are not responsible for delays or failures caused by events beyond our reasonable control. If the
              Service is unavailable for more than 30 days in a row, you may request a refund of your unused paid
              credits.
            </li>
            <li>
              These documents are available in several languages. If versions differ, the English version prevails,
              except where the mandatory law of your country of residence requires the version in your language to
              apply, or where the translation is more favourable to you as a consumer.
            </li>
          </ul>
        )
      },
      {
        heading: "13. Contact",
        body: (
          <p>
            Questions about these Terms: {contact(site)}. See also our {docLink("privacy", "Privacy Policy")} and{" "}
            {docLink("refund", "Refund Policy")}.
          </p>
        )
      }
    ]
  };
}

function privacyPolicy(site: SiteInfo): LegalDocument {
  return {
    title: "Privacy Policy",
    summary:
      "What personal data Lorvok collects, why, which service providers process it, how long it is kept, and how to access or delete it.",
    sections: [
      {
        heading: "1. Who is responsible",
        body: (
          <>
            <p>
              The data controller is {operator(site)}. We are based in {site.entityCountry} and access your data from
              there. Contact us about privacy at {contact(site)}; we acknowledge privacy complaints within 30 days.
            </p>
            {representatives(site)}
          </>
        )
      },
      {
        heading: "2. Data we collect",
        body: (
          <ul>
            <li>
              <strong>Account data:</strong> your email address and, if you set one, a hashed password (managed by our
              authentication provider), the date you joined, and a record of when you confirmed you are 18 or older
              and accepted the Terms (with the version you accepted). If you sign in with Google or Discord, that
              service shares with Supabase, our authentication provider, your email address, your account id there and
              the name and profile picture set there; Supabase stores them with your account. We don’t show that name
              or picture on Lorvok, and we never post anything to Google or Discord.
            </li>
            <li>
              <strong>Profile data:</strong> your public display name, bio and avatar, if you add them (an avatar is
              either a picture you upload or one generated from your description).
            </li>
            <li>
              <strong>Your content:</strong> story ideas and prompts, generated novels and images, your edits, your
              AI-editor requests (kept to diagnose the editor), comments, likes, users you block, your notifications,
              and reports you file (including any name and contact details you give).
            </li>
            <li>
              <strong>Reading activity:</strong> which novels you opened and finished, used for view and completion
              counts.
            </li>
            <li>
              <strong>Credits and purchases:</strong> your credit balance and history, and for each purchase the{" "}
              {MERCHANT_OF_RECORD} payment id, the pack, its price, the amount paid and currency, its refund or chargeback
              status, your account email, and your confirmations at checkout (18+, acceptance of the Terms and Refund
              Policy, and your request to start the Service immediately). We never receive your card details.
            </li>
            <li>
              <strong>Technical data:</strong> your IP address, used briefly for rate limiting and to check your
              region, and not stored by us (our hosting providers keep standard server logs); crash reports, which are
              not linked to your account; and feedback you send, with your browser details and any screenshots you
              attach.
            </li>
            <li>
              <strong>Usage analytics:</strong> if you accept analytics cookies — pages viewed, clicks and
              features used, and your approximate location (country and city, which PostHog works out from your IP
              address; the IP address itself is not stored), linked to your account id, with your username,
              interface language and sign-up date (see section 8). If you don’t, only an anonymous count: the same
              page and click events without any cookie or browser storage, never linked to you or your account —
              PostHog tells visits apart with a privacy-preserving value it calculates on its servers. In both cases
              the events also note the ad campaign or link that brought you, if any (campaign tags such as
              utm_source, and ad click ids). Our servers also count paid actions anonymously (which action, whether
              it completed, failed or was cancelled, how many credits it took and what it cost us), with no account
              id and no other identifier or reference that links them to you, your account or your novels.
            </li>
          </ul>
        )
      },
      {
        heading: "3. Why we use it and on what legal basis",
        body: (
          <>
            <ul>
              <li>
                to provide the Service — create your account, generate, store and show your novels, and send
                essential account emails (performance of our contract with you);
              </li>
              <li>to process purchases and keep an accurate credit ledger (performance of our contract);</li>
              <li>
                to keep the Service safe — check content, handle reports, prevent fraud and abuse, and fix errors (our
                legitimate interest in a safe, working service);
              </li>
              <li>
                to answer your messages (our contract with you, or our legitimate interest in answering people who
                write to us);
              </li>
              <li>
                to understand how the Service is used and improve it (your consent, for analytics with cookies; our
                legitimate interest, for the anonymous counts described in section 2);
              </li>
              <li>to comply with the law, for example tax rules or requests from authorities (legal obligation).</li>
            </ul>
            <p>
              To create an account you must give an email address and a password, or sign in with Google or Discord;
              without one of these we cannot provide an account. Everything else is optional.
            </p>
          </>
        )
      },
      {
        heading: "4. Automated checks",
        body: (
          <p>
            We check content automatically (a keyword filter and an AI classifier) before it is generated or
            published. A request that fails is refused and nothing is charged; contact us to have a person review it.
            We do not make decisions with legal or similarly significant effects on you — such as closing your account
            — solely by automated means, except that an account is suspended automatically when a payment for credits
            already used is reversed; contact us and a person will review it.
          </p>
        )
      },
      {
        heading: "5. Service providers we share data with",
        body: (
          <>
            <p>We do not sell your personal data. We share it only with providers that process it for us:</p>
            <ul>
              <li>
                <strong>AI models</strong> — Anthropic and OpenAI (United States): your prompts, story text, comments
                and profile text, to generate and edit novels and to check content against our rules. Under their API
                terms they do not use this data to train their models.
              </li>
              <li>
                <strong>Image generation</strong> — fal.ai and the image models it hosts (United States), and OpenAI
                when used: image descriptions from your story or your avatar description.
              </li>
              <li>
                <strong>Hosting, database and storage</strong> — Supabase (accounts, data and images) and Render
                (servers), both United States companies.
              </li>
              <li>
                <strong>Domain, network and email routing</strong> — Cloudflare (United States): our domain and DNS;
                email sent to our support address passes through Cloudflare to our mailbox provider.
              </li>
              <li>
                <strong>Email delivery</strong> — Resend (United States): account and sign-in emails, purchase
                confirmations and notices about moderation decisions.
              </li>
              <li>
                <strong>Payments</strong> — {MERCHANT_OF_RECORD}, our Merchant of Record: everything needed to take
                your payment, handle taxes, invoices and refunds. {MERCHANT_OF_RECORD} processes this data under its
                own privacy policy. On the Pricing page its checkout loads and may use its own cookies for payment
                security and fraud prevention.
              </li>
              <li>
                <strong>Analytics</strong> — PostHog (United States; data stored in its EU cloud): usage events
                with your consent, and the anonymous counts described in section 2.
              </li>
              <li>
                <strong>Team notifications</strong> — Discord (United States), a private team channel: feedback and
                reports you send, together with your account email or id, any contact you give, your browser details
                and screenshots you attach; and alerts about chargebacks or payment reversals, including the buyer’s
                account email.
              </li>
            </ul>
            <p>
              Your data is therefore processed outside your country, including in the United States. Where the law
              requires it, transfers rely on the EU–US Data Privacy Framework (for certified providers) or the EU
              Standard Contractual Clauses; email us for a copy. We may also disclose data when required by law.
            </p>
          </>
        )
      },
      {
        heading: "6. How long we keep it",
        body: (
          <>
            <ul>
              <li>Account data and content: while your account is open.</li>
              <li>
                Novels you delete are hidden at once and kept in storage until you delete your account; email us to
                have them erased sooner. Images are served from public links, so anyone with the exact link can open
                an image, even one from a private draft. Earlier versions of images you replaced may remain in storage;
                email us to have them removed.
              </li>
              <li>Finished generation-job records: deleted automatically about 24 hours after they finish.</li>
              <li>
                When you delete your account we immediately erase your profile, avatar, novels and their current
                images, comments, likes, blocks, notifications, reading activity, AI-editor requests and credit
                balance, and remove your email from feedback and reports you sent.
              </li>
              <li>
                Kept after deletion, without your profile or content: anonymised cost and error logs; the content of
                feedback and reports you sent; and, if your account was suspended, your email address so the
                suspension stays in effect.
              </li>
              <li>
                Messages already posted to our team channel and analytics data are deleted on request; email us.
                Database backups are kept by our provider for a limited period.
              </li>
              <li>
                Invoices and payment records are kept by {MERCHANT_OF_RECORD}, our Merchant of Record, for as long as tax law
                requires. AI providers keep API data only for the limited period their terms allow for abuse
                monitoring.
              </li>
            </ul>
          </>
        )
      },
      {
        heading: "7. Your rights",
        body: (
          <>
            <p>
              Depending on where you live you may have the right to access, correct, export (data portability) or
              delete your data, to restrict its processing, and to withdraw consent at any time (for analytics, with
              the “Cookie settings” link in the footer). You can edit your profile and delete your account yourself;
              for anything else email {mail(site.supportEmail)} — we reply within 30 days.
            </p>
            <p>
              <strong>Right to object.</strong> Where we rely on our legitimate interests (safety, abuse prevention,
              error logs), you can object at any time on grounds relating to your situation by emailing{" "}
              {mail(site.supportEmail)}.
            </p>
            <p>You also have the right to complain to your local data-protection authority.</p>
          </>
        )
      },
      {
        heading: "8. Cookies and local storage",
        body: (
          <>
            <p>
              We use strictly necessary browser storage to keep you signed in and to remember your settings and
              progress (language, reading progress, liked and finished novels, sound, editor preferences, a cache of
              catalog data). These do not require consent.
            </p>
            <p>
              Analytics cookies (PostHog) are set only after you accept them in the cookie banner. Without your
              consent PostHog stores nothing in your browser and only counts visits anonymously (section 2). You can
              change your choice at any time with the “Cookie settings” link in the footer. The {MERCHANT_OF_RECORD} checkout on the
              Pricing page may set its own cookies for payment security (see section 5).
            </p>
          </>
        )
      },
      {
        heading: "9. Age and availability",
        body: (
          <p>
            Lorvok is for adults: you must be 18 or older to use it, and we do not knowingly collect data from anyone
            younger. If you believe someone under 18 has an account, contact us and we will delete it. The Service is
            not offered in the Russian Federation or the United Kingdom and is not directed at users there.
          </p>
        )
      },
      {
        heading: "10. Changes",
        body: (
          <p>
            We will post any update to this policy on this page and, for significant changes, notify you by email or
            on the site before they take effect.
          </p>
        )
      }
    ]
  };
}

function refundPolicy(site: SiteInfo): LegalDocument {
  return {
    title: "Refund Policy",
    summary: "When you can get your money back for a credit pack, and how to ask for it.",
    sections: [
      {
        heading: "1. 14 days to change your mind",
        body: (
          <>
            <p>
              You can withdraw from a purchase within <strong>14 days of buying it</strong>, without giving a reason.
              Because you asked us at checkout to start providing the Service immediately, if you withdraw you are
              refunded for the credits in the pack you have not used, calculated pro rata on the price you paid, and
              you pay only for the credits already used. Bonus credits that come with a larger pack are part of that
              pack, so the unused share is counted over all of its credits.
            </p>
            <p>
              If you have used all the credits in the pack, the Service has been fully provided and the right to
              withdraw no longer applies. The refunded credits are removed from your balance.
            </p>
          </>
        )
      },
      {
        heading: "2. Failed, cancelled and refused actions",
        body: (
          <p>
            If a generation or edit fails on our side, the credits reserved for it are returned to your balance
            automatically — you do not need to ask. A request refused by our automated checks is never charged. An
            action you cancel after it has started is not refunded, because the work has already been carried out. If
            you think an action was charged incorrectly, contact us and we will look into it.
          </p>
        )
      },
      {
        heading: "3. After 14 days",
        body: (
          <p>
            Credits do not expire, so after 14 days a purchase is no longer refundable, except where the law requires
            it, where the Service could not be provided because of a fault on our side, or where we close your account
            (unused paid credits are then refunded, as described in the <a href="/terms">Terms of Service</a>). Credits
            spent on content later removed for breaking the Terms are not returned.
          </p>
        )
      },
      {
        heading: "4. How to request a refund",
        body: (
          <p>
            Email {mail(site.supportEmail)} from your account email with the order number from your receipt email,
            or contact {MERCHANT_OF_RECORD} through the support details in that receipt. Refunds are issued by{" "}
            {MERCHANT_OF_RECORD}, our Merchant of Record, to the original payment method; {MERCHANT_OF_RECORD} reviews
            requests under its {external(MOR_BUYER_TERMS, "Buyer Terms")} and may ask us how many credits were
            used. Your bank
            may take 5–10 business days to show the refund. Credits already used are not refundable; if a refund is
            granted for credits that were already used, the account is suspended as described in the Terms.
          </p>
        )
      },
      {
        heading: "5. Chargebacks",
        body: (
          <p>
            Please contact us before disputing a payment with your bank — we will sort it out faster. If a payment is
            charged back, the credits bought with it are removed, and if they were already used the account is
            suspended until it is resolved.
          </p>
        )
      },
      {
        heading: "6. Your statutory rights",
        body: (
          <p>
            This policy does not limit any rights you have under the consumer law of your country, including your
            rights if the Service is faulty or not as described.
          </p>
        )
      }
    ]
  };
}
